import React, { useState, useMemo } from "react";
import { AddEducationHistory } from "./AddEducationHistory";
import { TEducationHistory } from "features/settings/types";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { useDelete } from "hooks/useDelete";
import { Popconfirm, Skeleton, Alert } from "antd";
import { ProfileNavigationButtons } from "./ProfileNavigationButtons";
import { useNavigate } from "react-router-dom";
import { getNextSection } from "../../services/profileNavigationService";
import { useQueryClient } from "react-query";
import { useProfileCompletion } from "features/settings/hooks/useProfileCompletion";

const deleteEndPointUrl = "delete-education-history/";

const isEducationComplete = (item: TEducationHistory): boolean => {
  return !!(
    item.school &&
    item.school.trim().length > 0 &&
    item.course &&
    item.course.trim().length > 0 &&
    item.degree_id &&
    item.grade &&
    item.start_date &&
    (item.is_current === 1 || (item.end_date && item.end_date !== "1970-01-01"))
  );
};

export const EducationHistory: React.FC<{
  data: TEducationHistory[];
  isLoading?: boolean;
}> = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchProfile } = useProfileCompletion();
  const [addHistory, setAddHistory] = useState(false);
  const [educationId, setEducationId] = useState<number>();
  const { removeData } = useDelete({
    queryKey: QUERY_KEY_FOR_TALENT_PROFILE,
    deleteEndPointUrl,
  });

  const hasCompleteEducation = useMemo(() => {
    return data.some((item) => isEducationComplete(item));
  }, [data]);

  const handleRemove = (id: number) => {
    removeData(id);
    queryClient.invalidateQueries([QUERY_KEY_FOR_TALENT_PROFILE]);
    setTimeout(() => refetchProfile(), 100);
  };

  const handleAddEducation = () => {
    setEducationId(undefined);
    setAddHistory(true);
  };

  const handleEducation = (id: number) => {
    setEducationId(id);
    setAddHistory(true);
  };

  const handleAddClose = () => {
    setAddHistory(false);
    setEducationId(undefined);
    queryClient.invalidateQueries([QUERY_KEY_FOR_TALENT_PROFILE]);
    setTimeout(() => refetchProfile(), 100);
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return "N/A";

    if (/^\d{4}$/.test(dateString)) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  };

  const handleNext = () => {
    // This will only be called if the button is enabled
    const nextSection = getNextSection(window.location.pathname);
    if (nextSection) {
      navigate(nextSection.link);
    }
  };

  return (
    <>
      <AddEducationHistory
        id={educationId}
        open={addHistory}
        handleClose={handleAddClose}
      />

      <div className="mt-6 max-w-5xl">
        {/* HEADER */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-base font-semibold text-primary">Education</h4>
            <p className="text-sm text-gray-500">
              Add your education background
            </p>
          </div>

          <button
            onClick={handleAddEducation}
            className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-md flex items-center gap-1 hover:bg-primary/90"
          >
            <i className="ri-add-line"></i>
            <span>Add New</span>
          </button>
        </div>

        {data.length === 0 && (
          <Alert
            message="Education Required"
            description="Please add at least one complete education entry to continue."
            type="error"
            showIcon
            className="mb-4 bg-red-50 border-red-200"
          />
        )}

        {/* LIST */}
        <Skeleton active loading={isLoading}>
          <div className="space-y-4">
            {data.map((item) => {
              const isComplete: boolean = isEducationComplete(item);

              return (
                <div
                  key={item.id}
                  className={`border rounded-lg px-4 py-4 transition ${!isComplete
                    ? "border-red-400 border-2 bg-red-50/5"
                    : "border-gray-200 bg-white hover:border-secondary"
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-medium text-gray-500">
                        {item.school}
                      </p>
                      <h4 className="text-sm font-semibold text-primary mt-1">
                        {item.course}
                      </h4>
                    </div>

                    <div className="flex items-center gap-3">
                      <i
                        onClick={() => handleEducation(item.id)}
                        className="ri-pencil-fill text-gray-400 cursor-pointer text-xl hover:text-primary"
                      ></i>

                      <Popconfirm
                        okButtonProps={{
                          style: {
                            backgroundColor: "#f5222d",
                            color: "#ffffff",
                          },
                        }}
                        title="Delete education history"
                        description="Are you sure to delete this education history?"
                        onConfirm={() => handleRemove(item.id)}
                      >
                        <i className="ri-delete-bin-line text-gray-400 cursor-pointer text-xl hover:text-red-600"></i>
                      </Popconfirm>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                    <i className="ri-time-line"></i>
                    <span>
                      {item.start_date && (
                        <>
                          {formatDate(item.start_date)}{" "}
                          {item.is_current === 0 ? "to" : ""}
                        </>
                      )}
                      {item.is_current === 0
                        ? ` ${formatDate(item.end_date)}`
                        : " Currently studying here"}
                    </span>
                  </div>

                  {!isComplete && (
                    <Alert
                      message="Missing Required Fields"
                      description="Please complete all required fields for this education entry."
                      type="error"
                      showIcon
                      className="mt-3 bg-red-50 border-red-200"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Skeleton>

        <div className="mt-6">
          <ProfileNavigationButtons
            hasForm={false}
            onNext={handleNext}
            nextButtonLabel="Next"
            disabled={!hasCompleteEducation}
          />
        </div>
      </div>
    </>
  );
};
